// ─────────────────────────────────────────────────────────────────────────────
// ALBUMS CONFIG — 相册分类。这是「加分类」唯一要改的文件。
//
//   imageBase  图片地址前缀。留空 = 用本站自己的 /photos/…（图片跟着网站部署）
//              以后若把图片挪到别处（对象存储 / 图片 CDN），只改这一个值，
//              然后重跑 `python scripts/build-albums.py` 重新生成清单即可。
//   categories 分类列表。加一个分类 = 加一行 + 在 public/photos/ 下建同名目录。
//   allLabel   导航上「全部」这一项显示什么字。
//
// 隐藏分类（不出现在首页，当「图床」用）见 albums.hidden.json —— 说明在下面。
// ─────────────────────────────────────────────────────────────────────────────

import albumsJson from './albums.config.json'
import hiddenJson from './albums.hidden.json'

export interface AlbumCategory {
  id: string
  name: string
  order?: number
}

export interface AlbumsConfig {
  imageBase: string
  allLabel: string
  categories: AlbumCategory[]
}

export const albumsConfig = albumsJson as AlbumsConfig

// 「全部」是一个虚拟分类，不对应任何目录，只用于导航上的「看全部」。
export const ALL_CATEGORY_ID = 'all'

// 按 order 排好序（order 缺省按 0 处理）。
// ⚠️ 这是**全量**分类（含隐藏分类），后台 /admin 用它 —— 后台要能看到隐藏分类才能管它。
//    首页导航请用下面的 VISIBLE_ALBUM_CATEGORIES。
export const ALBUM_CATEGORIES: AlbumCategory[] = [...albumsConfig.categories]
  .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))

// ── 隐藏分类（「图床」位）──────────────────────────────────────────────────────
// 列在 albums.hidden.json 里的分类：
//   ✅ 照片照常放进 public/photos/<id>/，随站点一起部署，
//      直链 https://pics.xhja.cn/photos/<id>/<文件名> 可直接引用（小图标 / 头像 …）
//   ✅ /admin 后台照常能看到、能上传、能删（只是站长自己看）
//   ❌ 不出现在首页画廊（GALLERY_DATA 已过滤，见 src/App.constant.ts）
//   ❌ 不出现在分类导航（见 src/components/Categories/Categories.tsx）
//
// 为什么不把 hidden 标记直接写进 albums.config.json：
//   后台 /admin 的「增删改分类」由服务器改成一次 git 提交，会**整个重写**
//   那个 json（只写回 id / name / order），任何自定义字段都会被丢掉。
//   所以隐藏名单单独放一个文件 —— 服务端从不碰它。
//
// ⚠️ 「隐藏」不等于「保密」：图片没有链接指向，所以搜索引擎和访客逛不到，
//    但知道完整 URL 的人可以直接打开。别往里放真正敏感的东西。
export const HIDDEN_CATEGORY_IDS: string[] = (hiddenJson as { hidden?: string[] }).hidden ?? []

export const isHiddenCategory = (id: string) => HIDDEN_CATEGORY_IDS.includes(id)

// 首页可见的分类：导航栏与画廊用这个（隐藏分类已被剔除）。
export const VISIBLE_ALBUM_CATEGORIES: AlbumCategory[] =
  ALBUM_CATEGORIES.filter((category) => !isHiddenCategory(category.id))

export const albumsLabels: Record<string, string> = ALBUM_CATEGORIES.reduce(
  (acc, item) => {
    acc[item.id] = item.name
    return acc
  },
  { [ALL_CATEGORY_ID]: albumsConfig.allLabel || '全部' } as Record<string, string>,
)

// 把清单里的相对文件路径拼成可用的图片地址。
// 例：imageBase 为空 + file `travel/2026-05-11_01.webp`
//     → `/photos/travel/2026-05-11_01.webp`
export const resolveImageUrl = (file: string) => {
  const base = (albumsConfig.imageBase || '').replace(/\/+$/, '')
  return `${base}/photos/${file}`
}

export const categoryName = (id: string) => albumsLabels[id] ?? id
